// components/dashboard/widgets/TempoMedio.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Spin, Empty, Progress } from 'antd';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';
import { useDashboardData } from '../../../hooks/useDashboardData';

const TempoMedio = ({ endpoint, filtros, formato }) => {
  const { dados, loading, error } = useDashboardData(endpoint, filtros);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin size="large" />
        <p style={{ marginTop: 16, color: '#666' }}>Carregando tempo médio...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '20px',
        textAlign: 'center',
        backgroundColor: '#fff2f0',
        border: '1px solid #ffccc7',
        borderRadius: '6px',
        color: '#a8071a'
      }}>
        <p style={{ margin: '0 0 4px 0', fontWeight: 'bold' }}>
          Erro ao carregar tempo médio
        </p>
        <small style={{ color: '#cf1322' }}>
          {error?.message || String(error)}
        </small>
      </div>
    );
  }

  if (!dados || dados.length === 0) {
    return <Empty description="Nenhum dado de tempo encontrado" />;
  }

  // Função para determinar status do tempo
  const getStatusTempo = (dias) => {
    if (dias <= 90) return 'excelente';
    if (dias <= 120) return 'bom';
    if (dias <= 180) return 'regular';
    return 'atencao';
  };

  // Formatar dados
  const dadosFormatados = dados
    .map((item) => {
      const diasMedios = parseFloat(item.dias_medios) || 0;
      const cursoAbrev = item.curso
        ? item.curso.substring(0, 20) + (item.curso.length > 20 ? '...' : '')
        : 'N/A';

      return {
        curso: item.curso,
        dias_medios: diasMedios,
        curso_abrev: cursoAbrev,
        meses_medios: diasMedios / 30,
        semanas_medias: diasMedios / 7,
        status: getStatusTempo(diasMedios),
      };
    })
    .sort((a, b) => a.dias_medios - b.dias_medios);

  // Cores baseadas no status
  const getCor = (status) => {
    const cores = {
      excelente: '#52c41a',
      bom: '#1890ff',
      regular: '#fadb14',
      atencao: '#f5222d',
    };
    return cores[status] || '#8c8c8c';
  };

  // Estatísticas gerais
  const mediaTotalDias =
    dadosFormatados.reduce((sum, item) => sum + item.dias_medios, 0) /
    dadosFormatados.length;

  const menorTempo = Math.min(...dadosFormatados.map((item) => item.dias_medios));
  const maiorTempo = Math.max(...dadosFormatados.map((item) => item.dias_medios));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const meses = (data.dias_medios / 30).toFixed(1);
      const semanas = (data.dias_medios / 7).toFixed(1);

      return (
        <div
          style={{
            backgroundColor: '#fff',
            padding: '12px',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            minWidth: '200px',
          }}
        >
          <p style={{ margin: 0, fontWeight: 'bold', marginBottom: 8 }}>
            📚 {data.curso}
          </p>
          <div style={{ fontSize: 13 }}>
            <p style={{ margin: '4px 0', color: '#1890ff' }}>
              ⏱️ <strong>{data.dias_medios}</strong> dias
            </p>
            <p style={{ margin: '4px 0', color: '#666' }}>📅 {meses} meses</p>
            <p style={{ margin: '4px 0', color: '#666' }}>📆 {semanas} semanas</p>
            <div
              style={{
                marginTop: 8,
                padding: '4px 8px',
                borderRadius: '4px',
                backgroundColor: getCor(data.status) + '20',
                color: getCor(data.status),
                textAlign: 'center',
                fontSize: 12,
                fontWeight: 'bold',
              }}
            >
              {data.status.toUpperCase()}
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(
    PropTypes.shape({
      payload: PropTypes.shape({
        curso: PropTypes.string,
        dias_medios: PropTypes.number,
        status: PropTypes.string
      })
    })
  )
};

  // Formato de cards
  if (formato === 'cards') {
    return (
      <div style={{ height: 350, overflowY: 'auto' }}>
        {/* Estatísticas gerais */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-around',
            marginBottom: 16,
            padding: '12px',
            backgroundColor: '#fafafa',
            borderRadius: '6px',
            fontSize: 12,
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#1890ff', fontSize: 16 }}>
              {mediaTotalDias.toFixed(0)}
            </div>
            <div style={{ color: '#666' }}>Média Geral (dias)</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#52c41a', fontSize: 16 }}>
              {menorTempo.toFixed(0)}
            </div>
            <div style={{ color: '#666' }}>Menor Tempo</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontWeight: 'bold', color: '#f5222d', fontSize: 16 }}>
              {maiorTempo.toFixed(0)}
            </div>
            <div style={{ color: '#666' }}>Maior Tempo</div>
          </div>
        </div>

        {/* Cards por curso */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {dadosFormatados.map((item, index) => (
            <div
              key={index}
              style={{
                padding: '12px',
                border: `2px solid ${getCor(item.status)}`,
                borderRadius: '8px',
                backgroundColor: getCor(item.status) + '10',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 8,
                }}
              >
                <h4
                  style={{
                    margin: 0,
                    fontSize: 14,
                    color: '#262626',
                    fontWeight: 'bold',
                  }}
                >
                  {item.curso}
                </h4>
                <span
                  style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    backgroundColor: getCor(item.status),
                    color: 'white',
                    fontSize: 11,
                    fontWeight: 'bold',
                  }}
                >
                  {item.status.toUpperCase()}
                </span>
              </div>

              <div style={{ fontSize: 13, color: '#666' }}>
                <strong style={{ color: getCor(item.status) }}>
                  {item.dias_medios} dias
                </strong>
                {' ≈ '}
                <span>{item.meses_medios.toFixed(1)} meses</span>
              </div>

              <Progress
                percent={
                  maiorTempo > 0 ? (item.dias_medios / maiorTempo) * 100 : 0
                }
                strokeColor={getCor(item.status)}
                size="small"
                showInfo={false}
                style={{ marginTop: 8 }}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Formato de gráfico (padrão)
  return (
    <div style={{ height: 350 }}>
      {/* Estatísticas no topo */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-around',
          marginBottom: 16,
          padding: '8px',
          backgroundColor: '#fafafa',
          borderRadius: '4px',
          fontSize: 12,
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#1890ff' }}>
            {mediaTotalDias.toFixed(0)}
          </div>
          <div style={{ color: '#666' }}>Média (dias)</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#52c41a' }}>
            {(mediaTotalDias / 30).toFixed(1)}
          </div>
          <div style={{ color: '#666' }}>Média (meses)</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 'bold', color: '#fa8c16' }}>
            {maiorTempo - menorTempo}
          </div>
          <div style={{ color: '#666' }}>Variação</div>
        </div>
      </div>

      <div style={{ width: '100%', height: 'calc(350px - 90px)' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={dadosFormatados}
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="curso_abrev"
              tick={{ fontSize: 11 }}
              stroke="#666"
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              stroke="#666"
              label={{ value: 'Dias', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="dias_medios" name="Tempo Médio (dias)" radius={[4, 4, 0, 0]}>
              {dadosFormatados.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getCor(entry.status)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Legenda de status */}
      <div
        style={{
          marginTop: 8,
          fontSize: 11,
          color: '#666',
          display: 'flex',
          justifyContent: 'center',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#52c41a' }} />
          ≤90 dias (Excelente)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#1890ff' }} />
          91-120 dias (Bom)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#fadb14' }} />
          121-180 dias (Regular)
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#f5222d' }} />
          &gt;180 dias (Atenção)
        </span>
      </div>
    </div>
  );
};

// PropTypes
TempoMedio.displayName = 'TempoMedio';
TempoMedio.propTypes = {
  endpoint: PropTypes.string.isRequired,
  filtros: PropTypes.object,
  formato: PropTypes.oneOf(['grafico', 'cards']),
};

TempoMedio.defaultProps = {
  filtros: {},
  formato: 'grafico',
};

export default TempoMedio;
